import { READING_TYPES, CRACK_COOKIE_TOKEN_TYPES } from "helpers/appConstants";
import { Stack } from "@mui/material";
import {
  RedAlertIcon,
  ThresholdInputLabel,
  YellowAlertIcon
} from "../Thresholds/Thresholds.styles";
import ThresholdLine from "./ThresholdLine";
import { associateReadingType } from "helpers/variables";
import {
  ThresholdInterface
} from "context/initialState/Threshold";
import API from "api";
import Session from "helpers/session";
import { BodyProps, CellProps } from "components/shared/SimpleGrid/SimpleGrid";
import {
  uStatus,
  uPulse,
  uDiastolic,
  uSystolic,
  uHigh,
  uLow
} from "helpers/translationStringConstants";
import { useTranslation } from "react-i18next";
const { coordinator } = CRACK_COOKIE_TOKEN_TYPES;

const {
  bloodGlucose,
  bloodPressure,
  temperature,
  weight: WEIGHT,
  heartRate,
  oxygenSaturation,
  spirometry,
  activity
} = READING_TYPES;

export const generateInputs = (
  type: string | undefined,
  alertLevel: "moderate" | "high",
  disabled: boolean,
  onChangeCallback?: any
) => {
  console.log(type);
  switch (type) {
    case bloodPressure.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <Stack spacing={2}>
            <ThresholdLine
              type="Systolic"
              alertLevel={alertLevel}
              units="mmHg"
              isDisabled={disabled}
              onChangeFunc={onChangeCallback}
            />
            <ThresholdLine
              type="Diastolic"
              alertLevel={alertLevel}
              units="mmHg"
              isDisabled={disabled}
              onChangeFunc={onChangeCallback}
            />
          </Stack>
        </div>
      );
    case bloodGlucose.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Glucose"
            alertLevel={alertLevel}
            units="mg/dL"
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
    case spirometry.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <Stack spacing={2}>
            <ThresholdLine
              type="FVC"
              alertLevel={alertLevel}
              units="L"
              isDisabled={disabled}
              onChangeFunc={onChangeCallback}
            />
            <ThresholdLine
              type="FEV1"
              alertLevel={alertLevel}
              units="L"
              isDisabled={disabled}
              onChangeFunc={onChangeCallback}
            />
            <ThresholdLine
              type="FEV1/FVC"
              alertLevel={alertLevel}
              units="L"
              isDisabled={disabled}
              onChangeFunc={onChangeCallback}
            />
          </Stack>
        </div>
      );
    case oxygenSaturation.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <Stack spacing={2}>
            <ThresholdLine
              type={oxygenSaturation.display}
              alertLevel={alertLevel}
              units="%"
              isDisabled={disabled}
              onChangeFunc={onChangeCallback}
            />
          </Stack>
        </div>
      );
    case temperature.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Temperature"
            alertLevel={alertLevel}
            units="F"
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
    case WEIGHT.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Weight"
            alertLevel={alertLevel}
            units="LBS"
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
    case activity.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Activity"
            alertLevel={alertLevel}
            units="steps"
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );  
    case heartRate.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Pulse"
            alertLevel={alertLevel}
            units="bpm"
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
  }
};

export const getThresholds = (
  memberId: string | undefined,
  type: string,
  paramString?: string
) =>
  API.kore.readings.getThreshold(
    memberId || "",
    associateReadingType(type),
    Session.crackCookieToken(coordinator),
    paramString
  );

export const setThresholds = (threshold: ThresholdInterface) =>
  API.kore.readings.setThreshold(
    threshold,
    Session.crackCookieToken(coordinator)
  );

export const updateThreshold = (threshold: any) => {
  API.kore.readings.updateThreshold(
    threshold?._id || "",
    threshold.value,
    Session.crackCookieToken(coordinator)
  );
};

export const removeThresholds = (threshold: ThresholdInterface | undefined) =>
  API.kore.readings.removeThreshold(
    threshold?._id || "",
    Session.crackCookieToken(coordinator)
  );

export const GenerateTableHeaders = (type: any) => {
  const { t } = useTranslation();
  let thresholdTableHeaders: CellProps[] = [];
  switch (associateReadingType(type)) {
    case bloodPressure.abbreviation:
      thresholdTableHeaders.push(
        { content: `${t(uSystolic)} ${t(uLow)}`, align: "center" },
        { content: `${t(uSystolic)} ${t(uHigh)}`, align: "center" },
        { content: `${t(uDiastolic)} ${t(uLow)}`, align: "center" },
        { content: `${t(uDiastolic)} ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case bloodGlucose.abbreviation:
      thresholdTableHeaders.push(
        { content: `Blood Sugar ${t(uLow)}`, align: "center" },
        { content: `Blood Sugar ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case spirometry.abbreviation:
      thresholdTableHeaders.push(
        { content: `FVC ${t(uLow)}`, align: "center" },
        { content: `FVC ${t(uHigh)}`, align: "center" },
        { content: `FEV1 ${t(uLow)}`, align: "center" },
        { content: `FEV1 ${t(uHigh)}`, align: "center" },
        { content: `FEV1/FVC ${t(uLow)}`, align: "center" },
        { content: `FEV1/FVC ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case oxygenSaturation.abbreviation:
      thresholdTableHeaders.push(
        { content: `Blood Oxygen ${t(uLow)}`, align: "center" },
        { content: `Blood Oxygen ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case temperature.abbreviation:
      thresholdTableHeaders.push(
        { content: `Temperature ${t(uLow)}`, align: "center" },
        { content: `Temperature ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case WEIGHT.abbreviation:
      thresholdTableHeaders.push(
        { content: `Weight ${t(uLow)}`, align: "center" },
        { content: `Weight ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
      case activity.abbreviation:
      thresholdTableHeaders.push(
        { content: `Activity ${t(uLow)}`, align: "center" },
        { content: `Activity ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case heartRate.abbreviation:
      thresholdTableHeaders.push(
        { content: `Pulse Rate ${t(uLow)}`, align: "center" },
        { content: `Pulse Rate ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
  }
  return thresholdTableHeaders;
};

export const generateList = (rawList: ThresholdInterface[]): BodyProps[] => {
  return rawList.map((item) => {
    const {
      systolic,
      diastolic,
      pulse,
      glucose,
      FVC,
      FEV1,
      spo2,
      temp,
      weight,
      steps
    } = item.value;
    let breachIcon = <></>;
    let adjustedTableBody: CellProps[] = [];

    if (item.breachType === "high") {
      breachIcon = <RedAlertIcon />;
    } else if (item.breachType === "moderate") {
      breachIcon = <YellowAlertIcon />;
    }
    switch (item.readingType) {
      case bloodPressure.abbreviation:
        adjustedTableBody.push(
          {
            content: systolic?.low ? `${systolic?.low}` : "-",
            align: "center"
          },
          {
            content: systolic?.high ? `${systolic?.high}` : "-",
            align: "center"
          },
          {
            content: diastolic?.low ? `${diastolic?.low}` : "-",
            align: "center"
          },
          {
            content: diastolic?.high ? `${diastolic?.high}` : "-",
            align: "center"
          },
          { content: breachIcon, align: "center" }
        );
        break;
      case bloodGlucose.abbreviation:
        adjustedTableBody.push(
          { content: glucose?.low ? `${glucose?.low}` : "-", align: "center" },
          {
            content: glucose?.high ? `${glucose?.high}` : "-",
            align: "center"
          },
          { content: breachIcon, align: "center" }
        );
        break;
      case spirometry.abbreviation:
        adjustedTableBody.push(
          { content: FVC?.low ? `${FVC?.low}` : "-", align: "center" },
          { content: FVC?.high ? `${FVC?.high}` : "-", align: "center" },
          { content: FEV1?.low ? `${FEV1?.low}` : "-", align: "center" },
          { content: FEV1?.high ? `${FEV1?.high}` : "-", align: "center" },
          {
            content: `${FEV1 && FVC ? FEV1?.low / FVC?.low : "-"}`,
            align: "center"
          },
          {
            content: `${FEV1 && FVC ? FEV1?.high / FVC?.high : "-"}`,
            align: "center"
          },
          { content: breachIcon, align: "center" }
        );
        break;
      case oxygenSaturation.abbreviation:
        adjustedTableBody.push(
          { content: spo2?.low ? `${spo2?.low}` : "-", align: "center" },
          { content: spo2?.high ? `${spo2?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
      case temperature.abbreviation:
        adjustedTableBody.push(
          { content: temp?.low ? `${temp?.low}` : "-", align: "center" },
          { content: temp?.high ? `${temp?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
      case WEIGHT.abbreviation:
        adjustedTableBody.push(
          { content: weight?.low ? `${weight?.low}` : "-", align: "center" },
          { content: weight?.high ? `${weight?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
      case activity.abbreviation:
        adjustedTableBody.push(
          { content: steps?.low ? `${steps?.low}` : "-", align: "center" },
          { content: steps?.high ? `${steps?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
      case heartRate.abbreviation:
        adjustedTableBody.push(
          { content: pulse?.low ? `${pulse?.low}` : "-", align: "center" },
          { content: pulse?.high ? `${pulse?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
    }
    return { cells: adjustedTableBody };
  });
};
